import { useCallback, useMemo } from 'react';

import {
  CALENDAR_GRID_STEP_MINUTES_VALUES,
  CALENDAR_INTERVAL_HEIGHT_BY_SIZE,
  MAX_SELECTABLE_VISIBLE_COLUMN_COUNT,
  MIN_SELECTABLE_VISIBLE_COLUMN_COUNT,
} from '@pages/Calendar/data';
import type {
  AppointmentPreviewField,
  CalendarGridStepMinutes,
  CalendarIntervalHeightSize,
} from '@pages/Calendar/data';
import { useCalendarSettings as useCalendarDisplaySettings } from '@pages/Calendar/hooks';

// Настройки отображения журнала на странице Настройки журнала записей —
// те же, что в поповере журнала (task/11150). Хранилище одно
// (calendarDisplaySettings в localStorage на пользователя и филиал), поэтому
// значения, выбранные здесь, журнал показывает при следующем открытии.
export const useCalendarDisplaySettingsForm = () => {
  const {
    intervalHeightSize,
    gridStepMinutes,
    preferredMaxColumns,
    updateGridStepMinutes,
    updateIntervalHeightSize,
    updatePreferredMaxColumns,
    previewFields,
    updatePreviewFields,
  } = useCalendarDisplaySettings();

  const intervalHeightSizeOptions = useMemo(
    () =>
      (
        [
          ['small', 'Маленькая'],
          ['medium', 'Средняя'],
          ['large', 'Большая'],
        ] as const
      )
        .filter(([value]) => value in CALENDAR_INTERVAL_HEIGHT_BY_SIZE)
        .map(([value, label]) => ({
          label,
          value,
        })),
    []
  );

  const gridStepMinuteOptions = useMemo(
    () =>
      CALENDAR_GRID_STEP_MINUTES_VALUES.map((value) => ({
        label: value === 60 ? '1 час' : `${value} минут`,
        value,
      })),
    []
  );

  const preferredMaxColumnOptions = useMemo(
    () =>
      Array.from(
        {
          length:
            MAX_SELECTABLE_VISIBLE_COLUMN_COUNT -
            MIN_SELECTABLE_VISIBLE_COLUMN_COUNT +
            1,
        },
        (_, index) => {
          const value = MIN_SELECTABLE_VISIBLE_COLUMN_COUNT + index;

          return {
            label: String(value),
            value,
          };
        }
      ),
    []
  );

  const intervalHeightSizeChangeHandler = useCallback(
    (value: CalendarIntervalHeightSize) => {
      updateIntervalHeightSize(value);
    },
    [updateIntervalHeightSize]
  );

  const gridStepMinutesChangeHandler = useCallback(
    (value: CalendarGridStepMinutes) => {
      updateGridStepMinutes(value);
    },
    [updateGridStepMinutes]
  );

  const previewFieldsChangeHandler = useCallback(
    (value: AppointmentPreviewField[]) => {
      updatePreviewFields(value);
    },
    [updatePreviewFields]
  );

  const preferredMaxColumnsChangeHandler = useCallback(
    (value: number) => {
      updatePreferredMaxColumns(value);
    },
    [updatePreferredMaxColumns]
  );

  return {
    intervalHeightSize,
    intervalHeightSizeOptions,
    gridStepMinutes,
    gridStepMinuteOptions,
    preferredMaxColumns,
    preferredMaxColumnOptions,
    intervalHeightSizeChangeHandler,
    gridStepMinutesChangeHandler,
    preferredMaxColumnsChangeHandler,
    previewFields,
    previewFieldsChangeHandler,
  };
};
